import React, { useMemo, useCallback, useState } from 'react';
import { APP, BRAND_COLOR } from 'appenv';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import PlayButton from 'assets/play-button.png';
import CustomLightbox from './CustomLightbox';

const DescriptionContainer = styled.div`
  position: relative;
  line-height: 24px;
  overflow: ${({ isDescriptionBanner }) => (!isDescriptionBanner ? 'auto' : 'hidden')};
  height: 100%;
  flex-grow: 1;
  margin-block-start: 0;
  margin-block-end: 0;
  text-align: justify;
  text-overflow: ellipsis;

  & p {
    margin-block-start: 0;
    margin-block-end: 0;
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.2),
    rgba(255, 255, 255, 1)
  );
`;

const StyledH2 = styled.h2`
  margin-top: 12px;
  margin-bottom: 6px;
`;

const BoothNumContainer = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  margin-bottom: 0.5em;
  color: #484848;
`;

const ProfileBtn = styled.button`
  background: ${BRAND_COLOR || '#5C8DF3'};
  border-radius: 4px;
  font-family: Lato, sans-serif;
  border: 0;
  display: block;
  color: #FFFFFF;
  padding: 8px 16px;
  min-height: 32px;
  align-self: flex-start;
  text-decoration: unset;
  cursor: pointer;
`;

const Container = styled.div`
  top: 0;
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  padding: ${({ padding }) => padding}px;

  & h2, p {
    margin-top: 0;
    margin-bottom: 0;
    margin-block-start: 0;
    margin-block-end: 0;
  }
`;

const DescriptionImage = styled.div`
  background: url(${({ backgroundUrl }) => backgroundUrl});
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
`;

const DescriptionLink = styled.a`
  height: 100%;
  display: block;
  cursor: ${({ hasLink }) => (hasLink ? 'pointer' : 'unset')};
`;

const PlayButtonImage = styled.img`
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const ViewMoreButton = ({ handleClick }) => (
  <ProfileBtn
    onClick={() => handleClick('desk')}
  >
    View More
  </ProfileBtn>
);

ViewMoreButton.propTypes = {
  handleClick: PropTypes.func.isRequired,
};

const BoothNameContainer = ({ name, boothNumber }) => {
  const displayBoothNumber = useMemo(() => APP !== 'polyu618', []);
  return (
    <>
      <StyledH2>{name}</StyledH2>
      {displayBoothNumber && (
        <BoothNumContainer>
          {`Booth: ${boothNumber}`}
        </BoothNumContainer>
      )}
    </>
  );
};

BoothNameContainer.propTypes = {
  name: PropTypes.string.isRequired,
  boothNumber: PropTypes.string.isRequired,
};

const BannerTextElement = ({
  isDescriptionBanner,
  name,
  boothNumber,
  handleClick,
  description,
  isVideo,
}) => (
  <>
    {isDescriptionBanner && (
      <BoothNameContainer
        name={name}
        boothNumber={boothNumber}
      />
    )}
    <DescriptionContainer isDescriptionBanner={isDescriptionBanner}>
      {ReactHtmlParser(description)}
      {isDescriptionBanner && (<GradientOverlay />)}
      {!isDescriptionBanner && isVideo && (<PlayButtonImage src={PlayButton} />)}
    </DescriptionContainer>
    {isDescriptionBanner && <ViewMoreButton handleClick={handleClick} />}
  </>
);

BannerTextElement.propTypes = {
  isDescriptionBanner: PropTypes.bool.isRequired,
  name: PropTypes.string,
  boothNumber: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  handleClick: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  isVideo: PropTypes.bool.isRequired,
};

BannerTextElement.defaultProps = {
  boothNumber: '',
  name: '',
};

const BannerContentElement = ({ bannerData, name }) => {
  const { preview, file, video_preview: previewLight } = bannerData;
  const isVideoPreview = useMemo(() => preview?.type?.startsWith('video'), [preview?.type?.startsWith]);
  const isVideoFile = useMemo(() => file?.type?.startsWith('video'), [file?.type?.startsWith]);

  // eslint-disable-next-line camelcase
  const bannerImageUrl = preview?.data?.thumbnails?.find((thumb) => thumb?.url?.endsWith('thumbnail'))?.url
  // eslint-disable-next-line camelcase
    ?? file?.data?.thumbnails?.find((thumb) => thumb?.url?.endsWith('thumbnail'))?.url;
  // eslint-disable-next-line camelcase
  const bannerInlineVideoUrl = preview?.data?.full_url;
  const bannerInliveVideoPreviewThumbnail = previewLight?.data?.thumbnails?.find((thumb) => thumb?.url?.endsWith('thumbnail'))?.url;

  return (
    <>
      {isVideoPreview ? (
        <ReactPlayer
          url={bannerInlineVideoUrl}
          controls
          light={bannerInliveVideoPreviewThumbnail}
          playIcon={<img src={PlayButton} alt="Play Button" />}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                autoPlay: true,
                playsInline: true,
              },
            },
          }}
          style={{ zIndex: 100, backgroundColor: 'black' }}
        />
      ) : (
        <>
          <DescriptionImage backgroundUrl={bannerImageUrl} alt={`${name} Description`} />
          {isVideoFile && (<PlayButtonImage src={PlayButton} />)}
        </>
      )}
    </>
  );
};

BannerContentElement.propTypes = {
  bannerData: PropTypes.shape().isRequired,
  name: PropTypes.string,
};

BannerContentElement.defaultProps = {
  name: '',
};

const GenericBoothDescription = ({
  name,
  boothNumber,
  bannerData,
  handleClick,
  isDescriptionBanner,
  website,
}) => {
  const [showLightbox, setShowLightbox] = useState(false);

  const {
    link,
    file,
    preview,
    description,
  } = bannerData;

  const containerPadding = useMemo(() => {
    if (description) {
      return 16;
    }
    return 0;
  }, [description]);

  const bannerLink = useMemo(
    () => {
      // eslint-disable-next-line camelcase
      const noProtocolLink = (file?.data?.full_url || link || website || preview?.data?.full_url)?.replace(/\b(https:\/\/|http:\/\/)/, '');
      return noProtocolLink && `http://${noProtocolLink}`;
    },
    // eslint-disable-next-line camelcase
    [file?.data?.full_url, link, preview?.data?.full_url, website],
  );

  const fileType = useMemo(() => {
    if (file?.type?.startsWith('video')) {
      // eslint-disable-next-line camelcase
      return 'video';
    }
    if (file?.type === 'application/pdf') {
      return 'pdf';
    }
    if (link || website) {
      return 'link';
    }
    if (file?.type?.startsWith('image') || preview?.type?.startsWith('image')) {
      return 'image';
    }
    return null;
  }, [file?.type?.startsWith, file?.type, preview?.type?.startsWith, link, website]);

  const isInlineVideoBanner = useMemo(() => preview?.type?.startsWith('video'), [preview?.type?.startsWith]);

  const closeLightBox = useCallback(() => {
    setShowLightbox(false);
  }, [setShowLightbox]);

  const bannerClickAction = useCallback((event) => {
    event.preventDefault();
    if (!bannerLink) return;
    setShowLightbox(true);
  // eslint-disable-next-line camelcase
  }, [setShowLightbox, bannerLink]);
  const isVideo = useMemo(() => file?.type?.startsWith('video'), [file?.type?.startsWith]);

  const DescriptionElement = useCallback(() => (
    description ? (
      <BannerTextElement
        isDescriptionBanner={isDescriptionBanner}
        name={name}
        boothNumber={boothNumber}
        handleClick={handleClick}
        description={description}
        isVideo={isVideo}
      />
    ) : (
      <BannerContentElement
        name={name}
        bannerData={bannerData}
      />
    )
  ), [description, bannerData, handleClick, boothNumber, name, isDescriptionBanner, isVideo]);


  return (
    <Container padding={containerPadding}>
      {((isDescriptionBanner && description) || isInlineVideoBanner) ? (
        <DescriptionElement />
      ) : (
        <DescriptionLink onClick={bannerClickAction} hasLink={!!bannerLink}>
          <DescriptionElement />
        </DescriptionLink>
      )}
      <CustomLightbox
        src={bannerLink}
        onClose={closeLightBox}
        type={fileType}
        open={showLightbox}
      />
    </Container>
  );
};

GenericBoothDescription.propTypes = {
  name: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  boothNumber: PropTypes.string,
  bannerData: PropTypes.shape().isRequired,
  isDescriptionBanner: PropTypes.bool,
  website: PropTypes.string,
};

GenericBoothDescription.defaultProps = {
  name: '',
  boothNumber: '',
  isDescriptionBanner: false,
  website: '',
};

export default GenericBoothDescription;
